import { QuestToType, WarriorLogsType } from '../lib/types';
import { getUtcDayKey } from './utils';

/** How many quests are active on any given UTC day. */
export const DAILY_QUEST_COUNT = 3;

/** Prefix used to namespace quest rewards inside the shared warriorBonuses store. */
export const QUEST_CONDITION_PREFIX = 'quest:';

/** Build the reward `condition` id for a quest earned on a given UTC day. */
export function questConditionId(questId: string, dayKey: string): string {
  return `${QUEST_CONDITION_PREFIX}${questId}:${dayKey}`;
}

/** True when a warriorBonus condition belongs to the quest system (vs. a classic bonus). */
export function isQuestCondition(condition: string): boolean {
  return typeof condition === 'string' && condition.startsWith(QUEST_CONDITION_PREFIX);
}

function todayCombats(logs: WarriorLogsType) {
  const todayKey = getUtcDayKey(Date.now());
  return (logs.combats || []).filter(
    (c) => c._combat && c._combat.finished && getUtcDayKey(c._combat.finished) === todayKey,
  );
}

/** Number of combats won today. */
export function winsTodayCount(logs: WarriorLogsType): number {
  return todayCombats(logs).filter((c) => c.result === 'win').length;
}

/** Number of combats finished today (any result). */
export function fightsTodayCount(logs: WarriorLogsType): number {
  return todayCombats(logs).length;
}

/** 1 when the warrior has logged in today, else 0. */
export function loggedInTodayCount(logs: WarriorLogsType): number {
  const todayKey = getUtcDayKey(Date.now());
  return (logs.logins || []).some((e) => getUtcDayKey(e.timestamp) === todayKey) ? 1 : 0;
}

export type QuestEvent = 'login_today' | 'win_fights' | 'fight_battles';

/**
 * A quest evaluator returns the warrior's *current progress* toward the quest
 * (today), not a boolean. A quest is complete when its progress >= its target.
 * New quest types only need a counter added here.
 */
type ProgressEvaluator = (logs: WarriorLogsType) => number;

export const QUEST_EVALUATORS: Record<QuestEvent, ProgressEvaluator> = {
  login_today: loggedInTodayCount,
  win_fights: winsTodayCount,
  fight_battles: fightsTodayCount,
};

/** Current progress of a single quest, clamped to [0, target]. */
export function questProgress(quest: QuestToType, logs: WarriorLogsType): number {
  const evaluate = QUEST_EVALUATORS[quest.event as QuestEvent];
  if (!evaluate) return 0;
  return Math.min(evaluate(logs), quest.target);
}

/** True when the quest's progress has reached its target. */
export function isQuestComplete(quest: QuestToType, logs: WarriorLogsType): boolean {
  const evaluate = QUEST_EVALUATORS[quest.event as QuestEvent];
  if (!evaluate) return false;
  return evaluate(logs) >= quest.target;
}

/**
 * Deterministic, day-seeded hash of a string. Stable across client and backend
 * (no Date/Math.random), so both derive the same daily quest set from a day key.
 */
function hashString(str: string): number {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/**
 * Pick the day's active quests from the full pool. The selection is a pure
 * function of the pool and the UTC day key, so the client (for progress bars)
 * and the backend (for awarding) always agree without any stored "today's set".
 */
export function getDailyQuests(
  pool: QuestToType[],
  dayKey: string = getUtcDayKey(Date.now()),
  count: number = DAILY_QUEST_COUNT,
): QuestToType[] {
  return [...pool]
    .sort((a, b) => hashString(`${dayKey}:${a.id}`) - hashString(`${dayKey}:${b.id}`))
    .slice(0, count);
}
